'use client';

import { useLocale } from 'next-intl';
import { Globe } from 'lucide-react';
import {
  sortLocalesForUser,
  getLocaleByCode,
} from '@/shared/constants/locales';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { cn } from '@/shared/lib/cn';
import { useSwitchAppLocale } from '../hooks/useSwitchAppLocale';

interface LanguageSelectorProps {
  className?: string;
  triggerClassName?: string;
}

/**
 * Language selector component for settings and auth screens.
 * For unauthenticated users (e.g. on the login screen) API sync is skipped —
 * the choice is persisted only in the NEXT_LOCALE cookie and then reconciled
 * with the API after a successful login.
 */
export function LanguageSelector({
  className,
  triggerClassName,
}: LanguageSelectorProps) {
  const currentLocale = useLocale();
  const { switchLocale: handleLocaleChange, isUpdating } = useSwitchAppLocale();

  const sortedLocales = sortLocalesForUser(currentLocale);
  const currentLocaleData = getLocaleByCode(currentLocale);

  return (
    <div className={className}>
      <Select
        value={currentLocale}
        onValueChange={handleLocaleChange}
        disabled={isUpdating}
      >
        <SelectTrigger className={cn('w-full', triggerClassName)}>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-current" />
            <SelectValue>
              {currentLocaleData?.nativeName || currentLocale.toUpperCase()}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {sortedLocales.map((locale) => (
            <SelectItem key={locale.code} value={locale.code}>
              <div className="flex items-center gap-2">
                <span>{locale.nativeName}</span>
                {locale.code !== locale.name.toLowerCase().slice(0, 2) && (
                  <span className="text-list-item-meta text-muted-foreground">
                    ({locale.name})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
